import { useState, useEffect, memo, useContext } from "react";
import { AuthContext } from "../../context/authContext";
import { db, timestamp, fieldValue } from "../../utils/firebase";
import { v4 as uuidv4 } from "uuid";
import MyCheckbox from "../MyCheckbox/MyCheckbox";
import ContactPersons from "./ContactPersons";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { countries } from "../../utils/countries";
import { BiErrorCircle } from "react-icons/bi";
import { CgClose } from "react-icons/cg";
import "../../styles/Customers.css";

const AddCustomers = ({ isAddingCustomer, setIsAddingCustomer }) => {
  const [customerType, setCustomerType] = useState("Company");
  const [companyName, setCompanyName] = useState("");
  const [personalDetails, setPersonalDetails] = useState([
    {
      id: uuidv4(),
      name: "",
      email: "",
      phoneNumber1: "+255",
      phoneNumber2: "",
      role: "primary",
      dob: "",
    },
  ]);
  const [employeeDetails, setEmployeeDetails] = useState([
    {
      id: uuidv4(),
      name: "",
      email: "",
      phoneNumber1: "+255",
      phoneNumber2: "",
      role: "Owner",
      dob: "",
    },
  ]);
  const [address, setAddress] = useState({
    physical_address: "",
    city: "",
    country: "",
  });
  const [tin_no, setTin_no] = useState("");
  const [vrn_no, setVrn_no] = useState("");
  const [interestedProductGroups, setInterestedProductGroups] = useState([]);
  const [otherProductGroups, setOtherProductGroups] = useState("");
  const [allProductsGroupsSelected, setAllProductsGroupsSelected] =
    useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [allProductGroups, setAllProductGroups] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const {
    currentUser,
    uniqueTins,
    uniquePhoneNumbers,
    fetchUniqueTins,
    fetchUniquePhoneNumbers,
  } = useContext(AuthContext);

  // Function to fetch cities of selected Country
  const fetchCitiesByCountry = async (countryCode) => {
    try {
      const { City } = await import("country-state-city");
      const cities = City.getCitiesOfCountry(countryCode) || [];
      setAllCities(cities);
    } catch (err) {
      console.log(err.message);
      setAllCities([]);
    }
  };

  const isTinUnique = (tin) => {
    if (uniqueTins?.includes(tin)) return false;
    else return true;
  };

  const isContactUnique = (number) => {
    return !uniquePhoneNumbers?.includes(number);
  };

  const checkIfAllContactsAreUnique = () => {
    if (customerType === "Company") {
      const a = isContactUnique(employeeDetails[0].phoneNumber1);
      return a;
    } else if (customerType === "Individual") {
      const b = isContactUnique(personalDetails[0].phoneNumber1);
      return b;
    }

    return false;
  };

  // Function to add a customer
  const handleAdd = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (customerType === "Company" && !companyName) {
      setErrorMsg("Company Name is Required.");
      return;
    } else if (
      customerType === "Company" &&
      (!employeeDetails[employeeDetails.length - 1].name ||
        !employeeDetails[employeeDetails.length - 1].phoneNumber1)
    ) {
      setErrorMsg("Please fill Name and Phone Number of Employee.");
      return;
    } else if (
      customerType === "Individual" &&
      (!personalDetails[personalDetails.length - 1].name ||
        !personalDetails[personalDetails.length - 1].phoneNumber1)
    ) {
      setErrorMsg("Please fill Name and Phone Number of Customer.");
      return;
    } else if (!checkIfAllContactsAreUnique()) {
      setErrorMsg("Customer with this contact number already exists.");
      return;
    } else if (!address.physical_address) {
      setErrorMsg("All Address Fields are required.");
      return;
    } else if (tin_no.length > 0 && !isTinUnique(tin_no)) {
      setErrorMsg("Customer with this TIN number already exists.");
      return;
    } else if (interestedProductGroups.length < 1 && !otherProductGroups) {
      setErrorMsg("Please Enter Interested Product Group Details.");
      return;
    } else if (interestedProductGroups.length === 0) {
      setErrorMsg("Please Enter Interested Product Group Details.");
      return;
    } else {
      const id = uuidv4();
      const obj = {
        id,
        companyName,
        otherProductGroups,
        address,
        createdAt: timestamp,
        updatedAt: timestamp,
        customerType,
        TIN: tin_no,
        VRN: vrn_no,
        interestedProductGroups,
        addedById: currentUser?.id,
        addedByRole: currentUser?.role,
        contactPersons:
          customerType === "Individual"
            ? personalDetails[personalDetails.length - 1].name === ""
              ? personalDetails.slice(0, personalDetails.length - 1)
              : personalDetails
            : employeeDetails[employeeDetails.length - 1].name === ""
              ? employeeDetails.slice(0, employeeDetails.length - 1)
              : employeeDetails,
      };
      db.collection("customers")
        .doc(id)
        .set(obj)
        .then(() => {
          const phone_numbers =
            customerType === "Individual"
              ? personalDetails.map((pd) => pd.phoneNumber1)
              : employeeDetails.map((ed) => ed.phoneNumber1);
          db.collection("unique")
            .doc("phone")
            .update({
              value: fieldValue.arrayUnion(...[...phone_numbers]),
            })
            .then(() => {
              fetchUniqueTins();
            });
          tin_no &&
            db
              .collection("unique")
              .doc("tin")
              .update({
                value: fieldValue.arrayUnion(...[tin_no]),
              })
              .then(() => {
                fetchUniquePhoneNumbers();
              });
        });
      setIsAddingCustomer(false);
    }
  };

  useEffect(() => {
    let abortController = new AbortController();
    db.collection("productgroups")
      .orderBy("name", "asc")
      .onSnapshot((snapshot) => {
        setAllProductGroups(snapshot.docs.map((doc) => doc.data()));
      });
    setAllCountries(countries);
    return () => abortController.abort();
  }, []);

  return (
    <Modal
      show={isAddingCustomer}
      onHide={() => setIsAddingCustomer(false)}
      animation={false}
      centered
      className="full__modal"
    >
      <Modal.Header
        closeButton
        style={{ borderBottom: "none", paddingBottom: 0 }}
      >
        <Modal.Title>Add Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleAdd}>
          {/* Customer Type */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>Customer Type</Form.Label>
            <Form.Group>
              <Form.Check
                inline
                value="Company"
                checked={customerType === "Company"}
                onChange={(e) => {
                  setCustomerType(e.target.value);
                }}
                label="Company"
                type="radio"
              />
              <Form.Check
                inline
                value="Individual"
                checked={customerType === "Individual"}
                onChange={(e) => {
                  setCustomerType(e.target.value);
                }}
                label="Individual"
                type="radio"
              />
            </Form.Group>
          </Form.Group>

          {/* Personal Details */}
          {customerType === "Individual" && (
            <div className="mb-1">
              <ContactPersons
                customerType={customerType}
                details={
                  customerType === "Individual"
                    ? personalDetails
                    : employeeDetails
                }
                setDetails={
                  customerType === "Individual"
                    ? setPersonalDetails
                    : setEmployeeDetails
                }
              />
            </div>
          )}

          {/* Company Details */}
          {customerType === "Company" && (
            <>
              <Form.Group className="mb-1">
                <Form.Label style={{ fontWeight: 600 }}>
                  Company Name
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </Form.Group>
              <div className="mb-1">
                <ContactPersons
                  customerType={customerType}
                  details={
                    customerType === "Individual"
                      ? personalDetails
                      : employeeDetails
                  }
                  setDetails={
                    customerType === "Individual"
                      ? setPersonalDetails
                      : setEmployeeDetails
                  }
                />
              </div>
            </>
          )}

          {/* Address Details */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>Address Details</Form.Label>
            <Form.Control
              type="text"
              placeholder="Physical Address"
              value={address.physical_address}
              onChange={(e) =>
                setAddress({
                  ...address,
                  physical_address: e.target.value,
                })
              }
            />
            <Form.Group className="mt-3 d-flex">
              <Form.Select
                value={address.country || ""}
                onChange={(e) => {
                  const typed = e.target.value;
                  setAddress({ ...address, country: typed, city: "" });
                  setAllCities([]);
                  const matched = allCountries.find((c) => c.name === typed);
                  if (matched) fetchCitiesByCountry(matched.iso2);
                }}
              >
                <option value="" disabled>Select Country</option>
                {allCountries.map((country, index) => (
                  <option key={index} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </Form.Select>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <Form.Select
                value={address.city || ""}
                onChange={(e) =>
                  setAddress({ ...address, city: e.target.value })
                }
              >
                <option value="" disabled>Select City</option>
                {allCities.map((city, index) => (
                  <option key={index} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form.Group>

          {/* TIN and VRN numbers */}
          <Form.Group className="mb-3">
            <div className="d-flex">
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600 }}>TIN Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="TIN Number"
                  value={tin_no}
                  onChange={(e) => {
                    if (e.target.value.length <= 1) {
                      setTin_no(e.target.value);
                    } else {
                      if (e.target.value.length <= 11) {
                        const formatted = e.target.value.replaceAll("-", "");
                        setTin_no(
                          formatted.match(new RegExp(".{1,3}", "g")).join("-"),
                        );
                      }
                    }
                  }}
                />
              </Form.Group>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: 600 }}>VRN Number</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="VRN Number"
                    value={vrn_no}
                    onChange={(e) => {
                      if (e.target.value.length <= 11) {
                        let formatted = e.target.value;
                        if (formatted.length === 2) {
                          formatted = formatted + "-";
                        } else if (formatted.length === 9) {
                          formatted = formatted + "-H";
                        }
                        setVrn_no(formatted);
                      }
                    }}
                  />
                  {vrn_no.length > 1 && (
                    <InputGroup.Text
                      onClick={() => setVrn_no("")}
                      style={{ cursor: "pointer" }}
                    >
                      <CgClose style={{ fontSize: 20 }} />
                    </InputGroup.Text>
                  )}
                </InputGroup>
              </Form.Group>
            </div>
          </Form.Group>

          {/* Interested Product Groups */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>
              Interested Product Groups
            </Form.Label>
            <div className="checkbox__container">
              {allProductGroups?.map((category) => (
                <MyCheckbox
                  {...category}
                  key={category.id}
                  setInterestedProductGroups={setInterestedProductGroups}
                  interestedProductGroups={interestedProductGroups}
                />
              ))}
              <Form.Check
                inline
                checked={allProductsGroupsSelected}
                onChange={() => {
                  setAllProductsGroupsSelected(!allProductsGroupsSelected);
                  if (!allProductsGroupsSelected) {
                    setInterestedProductGroups(
                      allProductGroups.map((category) => category.id),
                    );
                  } else {
                    setInterestedProductGroups([]);
                  }
                }}
                label="Select All"
                type="checkbox"
              />
            </div>
          </Form.Group>

          {/* Others */}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>
              Other Product Groups
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Other Product Groups"
              value={otherProductGroups}
              onChange={(e) => setOtherProductGroups(e.target.value)}
            />
          </Form.Group>

          {/* Error message */}
          <Form.Group className="mb-3">
            {errorMsg && (
              <p className="error__msg" style={{ marginBottom: 20 }}>
                <BiErrorCircle />
                &nbsp;{errorMsg}
              </p>
            )}
          </Form.Group>

          {/* Buttons */}
          <Modal.Footer style={{ padding: 0, borderTop: "none" }}>
            <Button
              variant="danger"
              type="button"
              onClick={() => setIsAddingCustomer(false)}
            >
              Cancel
            </Button>
            <Button variant="success" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default memo(AddCustomers);
